import { Stack } from '@mui/material';
import { useMemo } from 'react';

import { Page } from '@occc-app/design-system';
import { hasAnyNonZeroProps } from '@occc/utils';

import { TobaccoSubmissionsTableContainer } from './components/tobacco-submissions-table/tobacco-submissions-table.container';
import { RetailerNetworkTobaccoWeeklyReimbursements } from './components/retailer-network-tobacco-weekly-reimbursements';
import { RetailerNetworkTobaccoLastWeeksSubmissionOverview } from './components/retailer-network-tobacco-last-weeks-submission-overview/retailer-network-tobacco-last-weeks-submission-overview';

import { withRetailerNetwork, WithRetailerNetworkProps } from '../hocs/with-retailer-network.hoc';
import { useBreadcrumbLinks } from '../hooks/use-breadcrumb-links.hook';
import { useRetailerNetworkScanDataTobaccoSubmissionsOverview } from '../hooks/use-retailer-network-scan-data-tobacco-submissions-overview.hook';
import { RetailerNetworkDataLoading } from '../components/retailer-network-data-loading.component';
import { RetailerNetworkDataPlaceholder } from '../components/retailer-network-data-placeholder.component';
import { tobaccoEmptyDataDescription, tobaccoEmptyDataTitle } from '../retailer-connect.copies';

const PAGE_TITLE = 'Tobacco Submissions';
const BREADCRUMB_LINKS = [{ label: 'Tobacco' }];

const RetailerNetworkTobaccoPage = ({ retailerNetwork }: WithRetailerNetworkProps) => {
  useBreadcrumbLinks(BREADCRUMB_LINKS);

  const { data, loading, error } = useRetailerNetworkScanDataTobaccoSubmissionsOverview(
    retailerNetwork.uuid,
  );

  const weeklyReimbursementsData = useMemo(() => {
    const timeSeries =
      data?.retailerNetworkScanDataTobaccoSubmissionsOverview?.estimatedReimbursementsTimeSeries;

    if (timeSeries) {
      return timeSeries.filter((item): item is NonNullable<typeof item> => item !== null);
    }

    return undefined;
  }, [data?.retailerNetworkScanDataTobaccoSubmissionsOverview?.estimatedReimbursementsTimeSeries]);

  const isZeroData = useMemo(() => {
    const overview = data?.retailerNetworkScanDataTobaccoSubmissionsOverview;
    if (!overview) return false;
    const {
      estimatedReimbursements,
      previousEstimatedReimbursements,
      totalSitesSubmitted,
      estimatedReimbursementsTimeSeries,
    } = overview;
    return !hasAnyNonZeroProps({
      estimatedReimbursements,
      previousEstimatedReimbursements,
      totalSitesSubmitted,
      estimatedReimbursementsTimeSeries,
    });
  }, [data]);

  return (
    <Page
      browserTabTitle={PAGE_TITLE}
      pageHeaderProps={{
        title: PAGE_TITLE,
        titleTestId: 'rc-tobacco-title',
      }}
      containerProps={{ variant: 'md' }}
    >
      <RetailerNetworkDataLoading isLoading={loading} hasData={!!data}>
        {isZeroData ? (
          <RetailerNetworkDataPlaceholder
            title={tobaccoEmptyDataTitle}
            description={tobaccoEmptyDataDescription}
          />
        ) : (
          !!data && (
            <Stack gap={1}>
              <RetailerNetworkTobaccoLastWeeksSubmissionOverview
                retailerNetworkScanDataTobaccoSubmissionsOverview={
                  data.retailerNetworkScanDataTobaccoSubmissionsOverview
                }
                error={error}
              />
              <RetailerNetworkTobaccoWeeklyReimbursements
                data={weeklyReimbursementsData}
                error={error}
              />
              <TobaccoSubmissionsTableContainer retailerNetworkUuid={retailerNetwork.uuid} />
            </Stack>
          )
        )}
      </RetailerNetworkDataLoading>
    </Page>
  );
};

export default withRetailerNetwork(RetailerNetworkTobaccoPage);
